import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  Box,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
} from 'chart.js';
import './StudentTimeline.css';
import useStore from '../store/useStore';

// Fixed: registered LineController and BarController
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
);

export default function StudentTimeline({ studentId, refreshKey }) {
  const en = useStore((s) => s.language) === 'en';
  const [events, setEvents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filteredEvents, setFilteredEvents] = useState([]);

  useEffect(() => {
    // Fetch Calendar Events (Profiles + Portfolio)
    fetch(`/api/students/${studentId}/calendar`)
      .then((res) => res.json())
      .then((data) => setEvents((prev) => [...prev, ...data]))
      .catch((err) => console.error('Error fetching calendar:', err));

    // Fetch all profiles including reassessment history for Chart
    fetch(`/api/profiles/student/${studentId}?all=true&active=1`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) =>
        setAssessments(
          data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        )
      )
      .catch((err) => console.error('Error fetching assessments:', err));

    // Fetch Student for Attendance
    fetch(`/api/students/${studentId}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.attendance) {
          setAttendance(data.attendance);
          // Add to events
          const attEvents = data.attendance.map((a) => ({
            id: `att-${a._id}`,
            title: `Attendance: ${a.status.toUpperCase()}`,
            start: a.date,
            type: 'attendance',
            color: a.status === 'present' ? '#10b981' : '#ef4444',
          }));
          setEvents((prev) => [...prev, ...attEvents]);
        }
      })
      .catch((err) => console.error('Error fetching student:', err));
  }, [studentId, refreshKey]);

  useEffect(() => {
    const dayEvents = events.filter((e) => {
      const eventDate = new Date(e.start);
      return eventDate.toDateString() === selectedDate.toDateString();
    });
    setFilteredEvents(dayEvents);
  }, [selectedDate, events]);

  // Calculate Absences between assessments
  const absenceCounts = assessments.map((assessment, index) => {
    const currentDate = new Date(assessment.createdAt);
    const prevDate =
      index === 0
        ? new Date(0) // Beginning of time for first one
        : new Date(assessments[index - 1].createdAt);

    const count = attendance.filter((a) => {
      const d = new Date(a.date);
      return d > prevDate && d <= currentDate && a.status === 'absent';
    }).length;

    return count;
  });

  // Cumulative words/phrases mastered — only ever rises (true growth story).
  let masteredRun = 0;
  const masteredCum = assessments.map((a) => {
    const m = (a.themes || []).reduce(
      (s, th) => s + (th.items || []).filter((i) => i.score === 2).length,
      0
    );
    masteredRun = Math.max(masteredRun, m);
    return masteredRun;
  });

  const mixedChartData = {
    labels: assessments.map((a) => new Date(a.createdAt).toLocaleDateString()),
    datasets: [
      {
        type: 'line',
        label: en ? 'Words mastered (total)' : 'Усвоено слов (всего)',
        data: masteredCum,
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.12)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: en
          ? 'Absences (since last exam)'
          : 'Пропуски (с прошлой оценки)',
        data: absenceCounts,
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        borderColor: '#ef4444',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        min: 0,
        ticks: { precision: 0 },
        title: {
          display: true,
          text: en ? 'Words mastered (total)' : 'Усвоено слов',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        min: 0,
        max: 10, // Assuming max 10 absences per period usually
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: en ? 'Absences' : 'Пропуски',
          color: '#ef4444',
        },
      },
    },
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayEvents = events.filter(
        (e) => new Date(e.start).toDateString() === date.toDateString()
      );
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
          {dayEvents.map((e, i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: e.color || 'primary.main',
                mx: 0.2,
              }}
            />
          ))}
        </Box>
      );
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        {en
          ? 'Words mastered vs. Attendance'
          : 'Усвоенные слова и посещаемость'}
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 3, mb: 6, borderRadius: 4, height: 400 }}
      >
        {assessments.length > 0 ? (
          <Chart type="bar" data={mixedChartData} options={chartOptions} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography color="text.secondary">
              Take more assessments to see the growth trend!
            </Typography>
          </Box>
        )}
      </Paper>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        Student Activity Calendar
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: 2, borderRadius: 4, flex: 1 }}
        >
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            tileContent={tileContent}
            className="custom-calendar"
          />
        </Paper>

        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: 3, borderRadius: 4, flex: 1, minHeight: 300 }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Events for {selectedDate.toLocaleDateString()}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {filteredEvents.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic', mt: 2 }}
            >
              No activity recorded for this day.
            </Typography>
          ) : (
            <List>
              {filteredEvents.map((event, idx) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {event.title}
                        </Typography>
                        <Chip
                          label={event.type}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: event.color,
                            color: 'white',
                            textTransform: 'uppercase',
                          }}
                        />
                      </Box>
                    }
                    secondary={new Date(event.start).toLocaleTimeString()}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
